
import { useState, useEffect } from 'react';

interface NetworkStatus {
  isOnline: boolean;
  isSlowConnection: boolean;
  connectionType?: string;
}

export const useNetworkStatus = () => {
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>({
    isOnline: navigator.onLine,
    isSlowConnection: false,
  });

  const [showSlowConnectionModal, setShowSlowConnectionModal] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setNetworkStatus(prev => ({ ...prev, isOnline: true }));
      setShowSlowConnectionModal(false);
    };

    const handleOffline = () => {
      setNetworkStatus(prev => ({ ...prev, isOnline: false }));
      setShowSlowConnectionModal(true);
    };

    // Check connection speed by measuring response time
    const checkConnectionSpeed = async () => {
      if (!navigator.onLine) return;

      const startTime = Date.now();
      try {
        // Use a small image to test connection speed
        const response = await fetch('/favicon.ico?' + Date.now(), {
          method: 'HEAD',
          cache: 'no-cache'
        });
        
        const endTime = Date.now();
        const responseTime = endTime - startTime;
        
        // Consider connection slow if response time > 3 seconds
        const isSlowConnection = responseTime > 3000;
        
        setNetworkStatus(prev => ({
          ...prev,
          isSlowConnection,
          connectionType: (navigator as any).connection?.effectiveType || 'unknown'
        }));

        // Show modal if connection is very slow
        if (isSlowConnection && !showSlowConnectionModal) {
          setShowSlowConnectionModal(true);
        }
      } catch (error) {
        console.log('Network check failed:', error);
        setNetworkStatus(prev => ({ ...prev, isSlowConnection: true }));
        setShowSlowConnectionModal(true);
      }
    };

    // Monitor connection changes
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check connection speed periodically
    const speedCheckInterval = setInterval(checkConnectionSpeed, 10000); // Check every 10 seconds

    // Initial speed check
    checkConnectionSpeed();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(speedCheckInterval);
    };
  }, [showSlowConnectionModal]);

  const dismissSlowConnectionModal = () => {
    setShowSlowConnectionModal(false);
  };

  return {
    ...networkStatus,
    showSlowConnectionModal,
    dismissSlowConnectionModal
  };
};
